import React, { useEffect, useMemo, useState } from 'react'
import Header from './Components/Header'
import Week from './Components/Week'
import Button from '../../Components/Buttons/Button'
import CourseStore from '../../App/Course/Store/CourseStore'
import Input from '../../Components/Input/Input'
import withStore from '../../Components/Unstated/withStore'
import { splitClassesInWeeks } from '../../App/Course/Helpers/Timings'
import { getField } from '../../Helpers/Misc'
import Reschedule from './Components/Reschedule'
import { filter, flatten, lt, pipe, propOr, values } from 'ramda'
import RescheduleAll from './Components/RescheduleAll'

const weeksInSinglePage = 3

const ClassTimings = ({
  courseStore: { state, deleteRegistration },
  userType,
}) => {
  const [page, changePage] = useState(0)
  const [students, updateStudents] = useState([])
  const [enrollment, setEnrollment] = useState({})
  const [selectedStudent, selectStudent] = useState(null)
  const [reschedule, initiateReschedule] = useState(null)
  const [rescheduleAll, initiateRescheduleAll] = useState(null)
  const [makeUpClasses, setMakeUpClasses] = useState(0)
  const [totalClasses, setTotalClasses] = useState(0)

  const enrollments = JSON.stringify(state.enrollments)

  useEffect(() => {
    const selectedEnrollment =
      state.enrollments && state.enrollments.length
        ? state.enrollments.slice(-1)[0]
        : {}

    selectStudent({
      value: selectedEnrollment,
      label: getField(selectedEnrollment, 'studentProfile.name'),
    })

    setEnrollment({
      ...selectedEnrollment,
      classes: splitClassesInWeeks(selectedEnrollment.classes),
    })

    if (userType === 'teacher') {
      updateStudents(
        state.enrollments.map((enrollment) => ({
          value: enrollment,
          label: enrollment.studentProfile.name,
        })) || [],
      )
    }
  }, [enrollments, state.enrollments, userType])

  useEffect(() => {
    if (enrollment.classes) {
      const firstWeekWithClass = Object.keys(
        enrollment.classes,
      ).findIndex((week) =>
        enrollment.classes[week].some(
          ({ endTime, status }) =>
            parseInt(endTime) > new Date().getTime() && status !== 'CANCELLED',
        ),
      )

      changePage(
        Math.max(Math.floor(firstWeekWithClass / weeksInSinglePage), 0),
      )
    }
  }, [enrollment.classes])

  useEffect(() => {
    if (enrollment.classes) {
      const allClasses = Object.keys(enrollment.classes).reduce(
        (merged, current) => [...merged, ...enrollment.classes[current]],
        [],
      )
      const makeUpClasses = allClasses.filter(({ makeupClass }) => makeupClass)

      setMakeUpClasses(
        Math.ceil((allClasses.length - makeUpClasses.length) / 4) -
          makeUpClasses.length,
      )
      setTotalClasses(allClasses.length - makeUpClasses.length)
    }
  }, [enrollment.classes])

  const remainingClasses = useMemo(
    () =>
      pipe(
        propOr({}, 'classes'),
        values,
        flatten,
        filter(pipe(propOr(0, 'startTime'), lt(Date.now()))),
        propOr(0, 'length'),
      )(enrollment),
    [enrollment],
  )

  return (
    <div className='classTimings'>
      <div className='headerContainer'>
        <Header
          enrollment={enrollment}
          remaining={remainingClasses}
          toggleRescheduleAll={() => initiateRescheduleAll(true)}
          endsOn={enrollment.courseEndTime}
          userType={userType}
        />

        {userType === 'teacher' && (
          <>
            <Input
              type={'select'}
              value={selectedStudent}
              options={students}
              onChange={(student) => {
                selectStudent(student)
                setEnrollment({
                  ...student.value,
                  classes: splitClassesInWeeks(student.value.classes),
                })
              }}
            />
            {selectedStudent && (
              <Button
                onClick={() => deleteRegistration(enrollment.id)}
                name={'Deregister'}
                type={'primary'}
                withConfirmation
              />
            )}
          </>
        )}
      </div>
      {enrollment.classes &&
        Object.keys(enrollment.classes)
          .sort((a, b) => {
            const class1 = enrollment?.classes[a][0]
            const class2 = enrollment?.classes[b][0]
            const class1Index = class1?.index || 0
            const class2Index = class2?.index || 0
            return class1Index - class2Index
          })
          .slice(page * weeksInSinglePage, (page + 1) * weeksInSinglePage)
          .map((week, i) => {
            return (
              <Week
                key={'weeklySlotsContainer' + i}
                index={page * weeksInSinglePage + i}
                data={enrollment.classes[week]}
                weekIndex={week}
                initiateReschedule={initiateReschedule}
                userType={userType}
                remainingMakeUpClasses={makeUpClasses}
                batchId={enrollment.batchId}
              />
            )
          })}
      {enrollment.classes &&
        Object.keys(enrollment.classes).length > weeksInSinglePage && (
          <div className='pageControl'>
            <Button
              onClick={() => changePage(Math.max(page - 1, 0))}
              name={'Previous'}
              type={'secondary'}
              disabled={page === 0}
            />
            <Button
              onClick={() =>
                changePage(
                  Math.min(
                    page + 1,
                    Math.floor(
                      (Object.keys(enrollment.classes).length - 1) /
                        weeksInSinglePage,
                    ),
                  ),
                )
              }
              name={'Next'}
              type={'primary'}
              disabled={
                page ===
                Math.floor(
                  (Object.keys(enrollment.classes).length - 1) /
                    weeksInSinglePage,
                )
              }
            />
          </div>
        )}
      {reschedule && (
        <Reschedule
          {...reschedule}
          livDemyID={state.livDemyID}
          remaining={makeUpClasses - 1}
          total={Math.ceil(totalClasses / 4)}
          close={() => initiateReschedule(null)}
        />
      )}
      {rescheduleAll && (
        <RescheduleAll
          courseRegistrationId={enrollment?.id}
          livDemyID={state.livDemyID}
          remaining={remainingClasses}
          close={() => initiateReschedule(null)}
        />
      )}
    </div>
  )
}

export default withStore([CourseStore])(ClassTimings)
